import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect } from 'react';
import { X, Calendar, ShoppingCart, Receipt, Users, CheckCircle, ArrowLeft, ChevronLeft, ChevronRight, Upload, Camera, Wallet, CreditCard, ArrowRight, FileText, Banknote, Plus, Search, Info, Coins, RefreshCcw } from 'lucide-react';
import { EXPENSE_CATEGORIES } from '../utils/expenseCategories';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import SupplierWizard from './SupplierWizard';
import AmountInput from './AmountInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

const ExpenseWizard = ({ isOpen, onClose, onSubmit, categories, suppliers, editMode = false, initialData = null }) => {
  const [currentStep, setCurrentStep] = useState(editMode ? 4 : 1);
  const [expenseType, setExpenseType] = useState('libre'); // 'libre' | 'invoice'
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [documentFilter, setDocumentFilter] = useState('all'); // 'all' | 'pending' | 'partial' | 'bl' | 'facture'
  const [localSuppliers, setLocalSuppliers] = useState(suppliers || []);

  useEffect(() => {
    setLocalSuppliers(suppliers || []);
  }, [suppliers]);

  const [formData, setFormData] = useState(
    editMode && initialData ? {
      category: initialData.category_name || initialData.category || '',
      subcategory: initialData.subcategory || '',
      date: initialData.date ? parseSafeDate(initialData.date) : new Date(),
      supplier: initialData.supplier_name || initialData.supplier || '',
      invoice_number: initialData.invoice_number || '',
      description: initialData.description || '',
      photo: initialData.photo_url || null,
      amount: initialData.amount || 0,
      payment_method: initialData.payment_method || 'cash',
      pay_state: initialData.pay_state || 'paid',
      paid_amount: initialData.paid_amount || ''
    } : {
      category: '',
      subcategory: '',
      date: new Date(),
      supplier: '',
      invoice_number: '',
      description: '',
      photo: null,
      amount: 0,
      payment_method: 'cash',
      pay_state: 'paid',
      paid_amount: ''
    }
  );
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [newSupplierDialogOpen, setNewSupplierDialogOpen] = useState(false);
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });

  // Reset wizard state when opening
  useEffect(() => {
    if (isOpen) {
      if (editMode && initialData) {
        setCurrentStep(4);
        setFormData({
          category: initialData.category_name || initialData.category || '',
          subcategory: initialData.subcategory || '',
          date: initialData.date ? parseSafeDate(initialData.date) : new Date(),
          supplier: initialData.supplier_name || initialData.supplier || '',
          invoice_number: initialData.invoice_number || '',
          description: initialData.description || '',
          photo: initialData.photo_url || null,
          amount: initialData.amount || 0,
          payment_method: initialData.payment_method || 'cash',
          pay_state: initialData.pay_state || 'paid',
          paid_amount: initialData.paid_amount || ''
        });
        setExpenseType('libre');
      } else {
        setCurrentStep(1);
        setFormData({
          category: '',
          subcategory: '',
          date: new Date(),
          supplier: '',
          invoice_number: '',
          description: '',
          photo: null,
          amount: 0,
          payment_method: 'cash',
          pay_state: 'paid',
          paid_amount: ''
        });
        setExpenseType('libre');
        setSelectedDocument(null);
        setSearchQuery('');
        setDocumentFilter('all');
      }
      setShowCalendar(false);
    }
  }, [isOpen, editMode, initialData]);

  // Fetch pending documents when switching to invoice type
  useEffect(() => {
    if (expenseType === 'invoice' && isOpen) {
      fetchPendingDocuments();
    }
  }, [expenseType, isOpen]);

  const fetchPendingDocuments = async () => {
    if (!currentUser?.id) return;
    setLoadingDocuments(true);
    try {
      const headers = { 'X-User-Id': currentUser.id };
      const res = await axios.get(`${API}/pending-documents`, { headers });
      setPendingDocuments(res.data || []);
    } catch (err) {
      console.error('Error fetching pending documents:', err);
      setPendingDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const mainCategories = EXPENSE_CATEGORIES;

  const paymentMethods = [
    { id: 'cash', label: 'Espèces', icon: Banknote },
    { id: 'card', label: 'Carte', icon: CreditCard },
    { id: 'transfer', label: 'Virement', icon: ArrowRight },
    { id: 'check', label: 'Chèque', icon: FileText },
    { id: 'traite', label: 'Traite', icon: Wallet }
  ];

  const handleCategorySelect = (categoryId) => {
    setFormData({ ...formData, category: categoryId, subcategory: '' });
  };

  const handleSubcategorySelect = (subcategoryId) => {
    setFormData({ ...formData, subcategory: subcategoryId });
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
    setShowCalendar(false);
  };

  const handlePaymentMethodSelect = (method) => {
    setFormData({ ...formData, payment_method: method });
  };

  const handleAmountChange = (value) => {
    setFormData({ ...formData, amount: value });
  };

  const handleDocumentSelect = (doc) => {
    setSelectedDocument(doc);
    // Pre-fill amount with remaining
    setFormData(prev => ({
      ...prev,
      amount: doc.remaining || 0,
      supplier: doc.supplier_name || '',
      invoice_number: doc.number || '',
      category: 'Achat'
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    const submitData = { ...formData, expenseType: expenseType === 'invoice' && selectedDocument ? 'invoice' : 'libre' };
    if (expenseType === 'invoice' && selectedDocument) {
      submitData.document = selectedDocument;
    }
    // Pass edit info directly in the data so it doesn't depend on parent state
    if (editMode && initialData) {
      submitData._editId = initialData.id;
      submitData._editCategoryId = initialData.category_id;
    }
    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      category: '', subcategory: '', date: new Date(), supplier: '',
      invoice_number: '', description: '', photo: null, amount: 0, payment_method: 'cash', pay_state: 'paid', paid_amount: ''
    });
    setCurrentStep(1);
    setShowCalendar(false);
    setExpenseType('libre');
    setSelectedDocument(null);
    setSearchQuery('');
    setDocumentFilter('all');
    onClose();
  };

  // Filter pending documents
  const filteredDocuments = pendingDocuments.filter(doc => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || (doc.number || '').toLowerCase().includes(q) ||
      (doc.supplier_name || '').toLowerCase().includes(q);
    if (documentFilter === 'pending') return matchesSearch && doc.status === 'pending';
    if (documentFilter === 'partial') return matchesSearch && doc.status === 'partial';
    if (documentFilter === 'bl') return matchesSearch && doc.type === 'bl';
    if (documentFilter === 'facture') return matchesSearch && doc.type === 'facture';
    return matchesSearch;
  });

  const renderCalendar = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = getDay(monthStart);
    const weekDays = ['S', 'D', 'L', 'M', 'M', 'J', 'V'];
    const adjustedStartDay = (startDayOfWeek + 1) % 7;

    return (
      <div className="bg-gray-50 rounded-2xl p-6 border-2 border-red-200">
        <div className="flex items-center justify-between mb-6">
          <button type="button" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))} className="p-2 hover:bg-gray-200 rounded-lg">
            <ChevronLeft size={20} />
          </button>
          <h4 className="text-xl font-bold text-gray-900 capitalize">
            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
          </h4>
          <button type="button" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))} className="p-2 hover:bg-gray-200 rounded-lg">
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-2 mb-3">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: adjustedStartDay }).map((_, idx) => <div key={`empty-${idx}`} />)}
          {daysInMonth.map((day) => {
            const isSelected = formData.date && format(day, 'yyyy-MM-dd') === format(formData.date, 'yyyy-MM-dd');
            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            return (
              <button key={day.toISOString()} type="button" onClick={() => handleDateSelect(day)}
                className={`h-12 rounded-xl font-medium text-base transition-all ${isSelected ? 'bg-red-500 text-white' : isToday ? 'bg-red-100 text-red-900 border-2 border-red-300' : 'text-gray-700 hover:bg-gray-200'}`}>
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
        {formData.date && (
          <div className="mt-6 text-center">
            <p className="text-lg font-bold text-gray-900 capitalize">{format(formData.date, 'EEEE d MMM. yyyy', { locale: fr })}</p>
          </div>
        )}
        <button type="button" onClick={() => setShowCalendar(false)} className="w-full mt-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold hover:bg-gray-50">
          Confirmer
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  const currentCategory = mainCategories.find(c => c.id === formData.category);
  const subcategories = currentCategory ? currentCategory.subcategories : [];
  const today = format(new Date(), 'dd MMM. yyyy', { locale: fr });

  // Dynamic stepper config
  const stepperLabels = expenseType === 'invoice'
    ? [{ step: 1, label: 'Type' }, { step: 2, label: 'Détails' }, { step: 3, label: 'Montant' }, { step: 4, label: 'Validation' }]
    : [{ step: 1, label: 'Catégorie' }, { step: 2, label: 'Détails' }, { step: 3, label: 'Montant' }, { step: 4, label: 'Validation' }];

  // Can continue from step 1?
  const canContinueStep1 = expenseType === 'invoice' || (expenseType === 'libre' && formData.category);
  // Can continue from step 2? (invoice type requires document selection)
  const canContinueStep2 = expenseType === 'libre' || (expenseType === 'invoice' && selectedDocument);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl z-10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Receipt className="text-red-600" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Nouvelle dépense</h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{today}</span>
            <button data-testid="expense-wizard-close" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="overflow-y-auto flex-1">
          {/* Stepper */}
          <div className="px-6 py-3 bg-white border-b border-gray-100 sticky top-0 z-10">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
            {stepperLabels.map(({ step, label }, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${step < currentStep ? 'bg-red-500 text-white' : step === currentStep ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                    {step < currentStep ? <CheckCircle size={20} /> : step}
                  </div>
                  <span className={`text-sm font-medium ${step <= currentStep ? 'text-red-600' : 'text-gray-400'}`}>{label}</span>
                </div>
                {index < 3 && <div className={`flex-1 h-0.5 mx-2 ${step < currentStep ? 'bg-red-500' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">

          {/* ===== STEP 1 ===== */}
          {currentStep === 1 && (
            <div className="space-y-4">
              {/* Type selection */}
              <div>
                <label className="block text-sm font-bold text-gray-600 mb-3 uppercase tracking-wider">Type de dépense</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button type="button" data-testid="expense-type-libre"
                    onClick={() => { setExpenseType('libre'); setSelectedDocument(null); }}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left flex items-start gap-4 ${expenseType === 'libre' ? 'bg-red-50 border-red-500' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className="absolute top-4 left-4">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${expenseType === 'libre' ? 'border-red-500' : 'border-gray-300'}`}>
                        {expenseType === 'libre' && <div className="w-2 h-2 rounded-full bg-red-500" />}
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ml-6 ${expenseType === 'libre' ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-500'}`}>
                      <Coins size={24} />
                    </div>
                    <div className="flex-1 mt-1">
                      <div className="font-bold text-gray-900 leading-tight mb-1">Dépense libre</div>
                      <div className="text-xs text-gray-500 leading-snug">Achat ou charge avec paiement<br />(espèces, banque, etc.)</div>
                    </div>
                  </button>
                  <button type="button" data-testid="expense-type-invoice"
                    onClick={() => { setExpenseType('invoice'); setFormData(prev => ({ ...prev, category: '' })); }}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left flex items-start gap-4 ${expenseType === 'invoice' ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className="absolute top-4 left-4">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${expenseType === 'invoice' ? 'border-blue-500' : 'border-gray-300'}`}>
                        {expenseType === 'invoice' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ml-6 ${expenseType === 'invoice' ? 'bg-blue-100 text-blue-500' : 'bg-gray-100 text-gray-500'}`}>
                      <FileText size={24} />
                    </div>
                    <div className="flex-1 mt-1">
                      <div className="font-bold text-gray-900 leading-tight mb-1">Paiement facture / BL</div>
                      <div className="text-xs text-gray-500 leading-snug">Régler une facture ou un BL<br />existant</div>
                    </div>
                  </button>
                  <button type="button" data-testid="expense-type-loss"
                    onClick={() => { setExpenseType('loss'); setSelectedDocument(null); }}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left flex items-start gap-4 ${expenseType === 'loss' ? 'bg-orange-50 border-orange-500' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className="absolute top-4 left-4">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${expenseType === 'loss' ? 'border-orange-500' : 'border-gray-300'}`}>
                        {expenseType === 'loss' && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ml-6 ${expenseType === 'loss' ? 'bg-orange-100 text-orange-500' : 'bg-gray-100 text-gray-500'}`}>
                      <RefreshCcw size={24} />
                    </div>
                    <div className="flex-1 mt-1">
                      <div className="font-bold text-gray-900 leading-tight mb-1">Pertes & ajustements</div>
                      <div className="text-xs text-gray-500 leading-snug">Périmés, casse, écarts,<br />régularisations...</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Info box */}
              {expenseType === 'libre' && (
                <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
                  <Info className="text-red-500 flex-shrink-0" size={24} />
                  <p className="text-sm text-red-600 font-medium">
                    Vous pourrez toujours lier une dépense à un document depuis les Factures & BL.
                  </p>
                </div>
              )}
              {expenseType === 'invoice' && (
                <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
                  <Info className="text-blue-500 flex-shrink-0" size={24} />
                  <p className="text-sm text-blue-600 font-medium">
                    Sélectionnez un document en attente pour enregistrer le paiement.
                  </p>
                </div>
              )}

              {/* Category selection */}
              {expenseType === 'libre' && (
                <>
                  <div>
                    <label className="block text-sm font-bold text-gray-600 mb-3 uppercase tracking-wider">Catégorie</label>
                    {formData.category ? (
                      <div className="space-y-3">
                        {mainCategories.filter(c => c.id === formData.category).map((category) => {
                          const Icon = category.icon;
                          return (
                            <div key={category.id} className={`w-full p-4 rounded-xl border-2 border-red-200 ${category.bgColor} flex items-center gap-4`}>
                              <div className={`w-14 h-14 rounded-xl bg-white/50 flex items-center justify-center flex-shrink-0 shadow-sm`}>
                                <Icon className={`${category.color}`} size={32} />
                              </div>
                              <div className="text-left flex-1">
                                <div className="font-bold text-xl text-gray-900 leading-tight mb-1">{category.label}</div>
                                <div className="text-sm text-gray-600">{category.description}</div>
                              </div>
                            </div>
                          );
                        })}
                        <button type="button" onClick={() => setFormData({ ...formData, category: '', subcategory: '' })}
                          className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50 flex items-center justify-center gap-2 transition-all">
                          <X size={18} /> Changer de catégorie
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        {mainCategories.map((category) => {
                          const Icon = category.icon;
                          return (
                            <button key={category.id} type="button" data-testid={`category-${category.id}`}
                              onClick={() => handleCategorySelect(category.id)}
                              className="w-full p-4 rounded-xl border border-gray-200 bg-white hover:border-red-200 hover:shadow-md transition-all flex items-start gap-4 text-left relative group">
                              <div className={`w-12 h-12 rounded-xl ${category.bgColor} flex items-center justify-center flex-shrink-0`}>
                                <Icon className={`${category.color}`} size={24} />
                              </div>
                              <div className="flex-1 mt-1 pr-6">
                                <div className="font-bold text-[13px] text-gray-900 leading-tight mb-1">{category.label}</div>
                                <div className="text-[11px] text-gray-500 leading-snug line-clamp-2">{category.description}</div>
                              </div>
                              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 group-hover:text-gray-500 transition-colors" size={16} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Sub-categories */}
                  {formData.category && subcategories.length > 0 && (
                    <div className="space-y-4 pt-2">
                      <label className="block text-sm font-bold text-gray-600 mb-2 uppercase tracking-wider">Sous-catégorie</label>
                      {formData.subcategory ? (
                        <div className="space-y-3">
                          <div className="px-6 py-3 rounded-full font-bold bg-red-500 text-white inline-block shadow-sm">
                            {formData.subcategory}
                          </div>
                          <button type="button" onClick={() => setFormData({ ...formData, subcategory: '' })}
                            className="w-full py-2 text-sm text-gray-500 font-medium hover:text-gray-800 flex items-center justify-center gap-1">
                            <X size={16} /> Changer de sous-catégorie
                          </button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          {subcategories.map((sub) => {
                            const Icon = sub.icon;
                            return (
                              <button
                                key={sub.id}
                                type="button"
                                onClick={() => handleSubcategorySelect(sub.id)}
                                data-testid={`subcat-${sub.id}`}
                                className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-red-300 hover:shadow-md transition-all text-left group"
                              >
                                <div className={`w-12 h-12 ${sub.bgColor} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                                  <Icon className={`${sub.color}`} size={24} strokeWidth={2.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-bold text-gray-900 leading-tight truncate mb-1">{sub.label}</div>
                                  {sub.desc && <div className="text-[11px] text-gray-500 leading-snug break-words line-clamp-2">{sub.desc}</div>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <span />
                <span className="text-xs text-gray-500 font-medium">Étape 1 sur 4</span>
                <button type="button" data-testid="step1-continue" onClick={handleNext} disabled={!canContinueStep1}
                  className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP 2 ===== */}
          {currentStep === 2 && expenseType === 'libre' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Détails de la dépense</h3>
                <p className="text-gray-500">Renseignez les informations complémentaires</p>
              </div>
              {/* Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Date</label>
                <button type="button" onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full bg-red-50 rounded-2xl p-6 border-2 border-red-300 hover:bg-red-100 transition-all">
                  <div className="flex items-center gap-3 text-lg justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="text-red-600" size={24} />
                      <span className="text-gray-900 font-medium capitalize">{format(formData.date, 'EEEE dd MMM. yyyy', { locale: fr })}</span>
                    </div>
                    <ChevronRight className="text-gray-400" size={20} />
                  </div>
                </button>
                {showCalendar && <div className="mt-4">{renderCalendar()}</div>}
              </div>
              {/* Supplier */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Fournisseur</label>
                <div className="space-y-3">
                  <select value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-500 bg-white">
                    <option value="">Sélectionnez un fournisseur...</option>
                    {localSuppliers && localSuppliers.length > 0 ? 
                      Array.from(new Map(localSuppliers.filter(s => s && s.name).map(s => [s.name.trim().toLowerCase(), s])).values())
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((s) => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    )) : <option disabled>Aucun fournisseur disponible</option>}
                  </select>
                  <button type="button" onClick={() => setNewSupplierDialogOpen(true)}
                    className="w-full p-4 border-2 border-dashed border-red-300 rounded-xl text-red-600 font-medium hover:bg-red-50 transition-all flex items-center justify-center gap-2">
                    <Plus size={20} /> Nouveau fournisseur
                  </button>
                </div>
              </div>
              {/* Invoice number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Numéro de facture (optionnel)</label>
                <input type="text" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  placeholder="Ex : FAC-2026-001" className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-500" />
              </div>
              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Description (optionnel)</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la dépense..." rows="3"
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-500 resize-none" />
              </div>
              {/* Photo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Pièce jointe (optionnel)</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" className="p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-red-300 hover:bg-red-50 transition-all flex flex-col items-center gap-2 text-gray-600">
                    <Camera size={24} /><span className="text-sm font-medium">Prendre une photo</span>
                  </button>
                  <button type="button" className="p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-red-300 hover:bg-red-50 transition-all flex flex-col items-center gap-2 text-gray-600">
                    <Upload size={24} /><span className="text-sm font-medium">Télécharger un fichier</span>
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button type="button" onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors">
                  <ArrowLeft size={16} /> Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 2 sur 4</span>
                <button type="button" data-testid="step2-continue" onClick={handleNext}
                  className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors">
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP 2 — Invoice: Document selection ===== */}
          {currentStep === 2 && expenseType === 'invoice' && (
            <div className="space-y-4">
              {/* Type reminder */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl border-2 border-gray-200 bg-white opacity-60">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                    <span className="font-bold text-sm text-gray-500">Dépense libre</span>
                  </div>
                </div>
                <div className="p-3 rounded-xl border-2 border-red-500 bg-red-50">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-red-500 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                    </div>
                    <span className="font-bold text-sm text-gray-900">Paiement facture / BL</span>
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
                <Info className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-red-700">
                  Sélectionnez un document en attente pour enregistrer le paiement. Une dépense liée sera créée automatiquement.
                </p>
              </div>

              {/* Search & Filters (hidden when a document is selected) */}
              {!selectedDocument && (
                <>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input type="text" data-testid="document-search" value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher un fournisseur, n° facture ou BL..."
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-500 text-sm" />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {[
                      { id: 'all', label: 'Tous' },
                      { id: 'pending', label: 'Non payé' },
                      { id: 'partial', label: 'Partielle' },
                      { id: 'bl', label: 'BL' },
                      { id: 'facture', label: 'Facture' },
                    ].map(f => (
                      <button key={f.id} type="button" data-testid={`filter-${f.id}`}
                        onClick={() => setDocumentFilter(f.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${documentFilter === f.id ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Document list OR selected document */}
              {selectedDocument ? (
                <div className="space-y-3">
                  {/* Selected document card */}
                  <div className="p-4 rounded-2xl border-2 bg-red-50 border-red-500 text-left" data-testid="selected-document-card">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{selectedDocument.number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedDocument.status === 'partial' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                            {selectedDocument.status === 'partial' ? 'Partielle' : 'Non payé'}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-red-600">{selectedDocument.supplier_name}</span>
                      </div>
                      {selectedDocument.due_date && (
                        <span className="text-xs text-gray-400">Échéance {selectedDocument.due_date}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      Total {selectedDocument.total?.toLocaleString('fr-FR')} DA
                      {selectedDocument.paid > 0 && <> &middot; Déjà payé {selectedDocument.paid?.toLocaleString('fr-FR')} DA</>}
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      Reste à payer {selectedDocument.remaining?.toLocaleString('fr-FR')} DA
                    </div>
                  </div>
                  {/* Deselect button */}
                  <button type="button" data-testid="deselect-document"
                    onClick={() => { setSelectedDocument(null); setFormData(prev => ({ ...prev, amount: 0, supplier: '' })); }}
                    className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 flex items-center justify-center gap-2 transition-all">
                    <X size={16} /> Changer de document
                  </button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[340px] overflow-y-auto">
                  {loadingDocuments ? (
                    <div className="text-center py-8 text-gray-400">Chargement...</div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">Aucun document trouvé</div>
                  ) : (
                    filteredDocuments.map((doc) => {
                      const statusLabel = doc.status === 'partial' ? 'Partielle' : 'Non payé';
                      const statusColor = doc.status === 'partial' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700';
                      return (
                        <button key={doc.id} type="button" data-testid={`document-${doc.id}`}
                          onClick={() => handleDocumentSelect(doc)}
                          className="w-full p-4 rounded-2xl border-2 transition-all text-left bg-white border-gray-200 hover:border-gray-300">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-900">{doc.number}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>{statusLabel}</span>
                              </div>
                              <span className="text-sm font-medium text-red-600">{doc.supplier_name}</span>
                            </div>
                            {doc.due_date && (
                              <span className="text-xs text-gray-400">Échéance {doc.due_date}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            Total {doc.total?.toLocaleString('fr-FR')} DA
                            {doc.paid > 0 && <> &middot; Déjà payé {doc.paid?.toLocaleString('fr-FR')} DA</>}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 mt-1">
                            Reste à payer {doc.remaining?.toLocaleString('fr-FR')} DA
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button type="button" onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors">
                  <ArrowLeft size={16} /> Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 2 sur 4</span>
                <button type="button" data-testid="step2-invoice-continue" onClick={handleNext} disabled={!canContinueStep2}
                  className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ===== STEP 3 — Amount & Payment ===== */}
          {currentStep === 3 && (
            <div className="space-y-4">
              {/* Invoice header */}
              {expenseType === 'invoice' && selectedDocument && (
                <div className="bg-red-50 rounded-2xl p-4 border border-red-200">
                  <div className="text-sm font-semibold text-red-700">Paiement de facture</div>
                  <div className="text-lg font-bold text-gray-900">{selectedDocument.number} &middot; {selectedDocument.supplier_name}</div>
                </div>
              )}

              {expenseType === 'libre' && (
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                  <div className="text-sm font-semibold text-gray-500">Dépense libre</div>
                </div>
              )}

              <div className="text-sm text-gray-500">Reste à payer</div>

              {/* Amount display */}
              <AmountInput value={formData.amount} onChange={handleAmountChange} accentColor="red" />



              {/* État du paiement */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">État du paiement</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'unpaid', label: 'Non payée' },
                    { value: 'paid', label: 'Payée' },
                    { value: 'partial', label: 'Partiellement payée' },
                  ].map(opt => (
                    <button key={opt.value} type="button" data-testid={`expense-pay-state-${opt.value}`}
                      onClick={() => setFormData({ ...formData, pay_state: opt.value })}
                      className={`py-3 px-2 rounded-xl border-2 text-center text-sm font-medium transition-all ${
                        formData.pay_state === opt.value
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {formData.pay_state === 'partial' && (
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                  <div className="text-sm font-semibold text-orange-800 mb-2">Montant payé (DA)</div>
                  <input
                    type="number"
                    value={formData.paid_amount}
                    onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                    placeholder="0"
                    className="w-full text-xl font-bold bg-white border-2 border-orange-200 rounded-lg p-3 text-orange-900 focus:outline-none focus:border-orange-500"
                  />
                </div>
              )}

              <button type="button" data-testid="step3-continue" onClick={handleNext}
                className="w-full py-4 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600">
                Continuer
              </button>
              <button type="button" onClick={handleBack}
                className="w-full py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold hover:bg-gray-50 flex items-center justify-center gap-2">
                <ArrowLeft size={20} /> Retour
              </button>
            </div>
          )}

          {/* ===== STEP 4 — Validation ===== */}
          {currentStep === 4 && !editMode && (
            <div className="space-y-4">
              {/* Success icon */}
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-red-500" size={48} />
                </div>
              </div>

              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {expenseType === 'invoice' ? 'Dépense enregistrée !' : 'Dépense enregistrée !'}
                </h3>
                <p className="text-gray-500">
                  {expenseType === 'invoice' ? 'Le paiement a été synchronisé.' : 'Votre dépense a été créée avec succès.'}
                </p>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-2xl p-6 space-y-3">
                <h4 className="text-sm font-semibold text-gray-500 uppercase">Récapitulatif</h4>

                {expenseType === 'invoice' && selectedDocument ? (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Type</span>
                      <span className="font-semibold text-gray-900">Paiement facture</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Document</span>
                      <span className="font-semibold text-gray-900">{selectedDocument.number}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Fournisseur</span>
                      <span className="font-semibold text-gray-900">{selectedDocument.supplier_name}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Montant payé</span>
                      <span className="font-bold text-2xl text-red-600">{formData.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Mode de paiement</span>
                      <span className="font-semibold text-gray-900">{paymentMethods.find(m => m.id === formData.payment_method)?.label}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">État</span>
                      <span className={`font-semibold ${formData.amount >= selectedDocument.remaining ? 'text-green-600' : 'text-orange-600'}`}>
                        {formData.amount >= selectedDocument.remaining ? 'Payée' : 'Partielle'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Type</span>
                      <span className="font-semibold text-gray-900">Dépense libre</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Catégorie</span>
                      <span className="font-semibold text-gray-900">{formData.category}</span>
                    </div>
                    {formData.subcategory && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-gray-600">Sous-catégorie</span>
                        <span className="font-semibold text-gray-900">{formData.subcategory}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Fournisseur</span>
                      <span className="font-semibold text-gray-900">{formData.supplier || '—'}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Montant</span>
                      <span className="font-bold text-2xl text-red-600">{formData.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Mode de paiement</span>
                      <span className="font-semibold text-gray-900">{paymentMethods.find(m => m.id === formData.payment_method)?.label}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600">État</span>
                      <span className={`font-semibold ${
                        formData.pay_state === 'paid' ? 'text-green-600' : formData.pay_state === 'partial' ? 'text-orange-600' : 'text-red-600'
                      }`}>
                        {formData.pay_state === 'paid' ? 'Payée' : formData.pay_state === 'partial' ? 'Partiellement payée' : 'Non payée'}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Info box */}
              <div className="bg-red-50 rounded-xl p-4 flex items-start gap-3">
                <Info className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-red-700">
                  {expenseType === 'invoice'
                    ? "Une dépense et un paiement ont été créés automatiquement. Le reste à payer du document est mis à jour."
                    : "La dépense a été créée. Aucun document n'est lié."}
                </p>
              </div>

              <button type="button" data-testid="step4-submit" onClick={handleSubmit}
                className="w-full py-4 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 flex items-center justify-center gap-2">
                <CheckCircle size={20} />
                Valider la dépense
              </button>
              <button type="button" onClick={handleBack}
                className="w-full py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold hover:bg-gray-50 flex items-center justify-center gap-2">
                <ArrowLeft size={20} /> Retour
              </button>
            </div>
          )}

          {/* ===== STEP 4 — Edit mode validation (existing) ===== */}
          {currentStep === 4 && editMode && (
            <div className="space-y-4">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Tout est correct ?</h3>
                <p className="text-gray-500">Relisez avant de valider</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Date</span>
                  <span className="font-semibold text-gray-900 capitalize">{format(formData.date, 'd MMM. yyyy', { locale: fr })}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Catégorie</span>
                  <span className="font-semibold text-gray-900">{formData.category}</span>
                </div>
                {formData.subcategory && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Sous-catégorie</span>
                    <span className="font-semibold text-gray-900">{formData.subcategory}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Montant</span>
                  <span className="font-bold text-2xl text-red-600">{formData.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-600">Paiement</span>
                  <span className="font-semibold text-gray-900">{paymentMethods.find(m => m.id === formData.payment_method)?.label || formData.payment_method}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-t border-gray-200">
                  <span className="text-gray-600">État du paiement</span>
                  <select
                    value={formData.pay_state}
                    onChange={(e) => setFormData({ ...formData, pay_state: e.target.value })}
                    className="p-2 border border-gray-300 rounded-lg bg-white text-sm font-semibold focus:outline-none focus:border-red-500"
                  >
                    <option value="paid">Payée</option>
                    <option value="partial">Partiellement payée</option>
                    <option value="unpaid">Non payée</option>
                  </select>
                </div>
              </div>
              <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="text-red-600 flex-shrink-0" size={24} />
                <span className="text-red-700 font-medium">Sera enregistrée dans votre tableau de bord</span>
              </div>
              <button type="button" onClick={handleSubmit}
                className="w-full py-4 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 flex items-center justify-center gap-2">
                <CheckCircle size={20} /> Modifier la dépense
              </button>
              <button type="button" onClick={handleBack}
                className="w-full py-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold hover:bg-gray-50 flex items-center justify-center gap-2">
                <ArrowLeft size={20} /> Modifier
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* SupplierWizard */}
      <SupplierWizard
        isOpen={newSupplierDialogOpen}
        onClose={() => setNewSupplierDialogOpen(false)}
        onSuccess={(newSupplier) => {
          setLocalSuppliers(prev => [...prev, newSupplier]);
          setFormData({ ...formData, supplier: newSupplier.name });
        }}
        currentUser={currentUser}
      />
    </div>
  );
};

export default ExpenseWizard;
